export class DateUtils {

	/**
	 * Indique si la date passée en paramètre est supérieure ou égale à la date d'hier
	 * @param date true si la date est supérieure ou égale à la date d'hier, false sinon
	 */
	static estDateSuperieureOuEgaleHier(date: Date): boolean {
		const hier = new Date();
		hier.setDate(hier.getDate() - 1);

		return date >= hier;
	}

	/**
	 * Fonction qui initialise à minuit, la date donnée.
	 * @param date La date que l'on veut initialiser.
	 * @returns {Date} la date avec les minutes, les secondes et les heures initialisées à zero.
	 */
	static toDateMidnight(date: number | string | Date): Date {
		const d = date instanceof Date ? date : new Date(date);
		return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
	}

	/**
	 * Vérification qu'une date est strictement inférieure à une autre (en
	 * tronquant à la journée).
	 * @param {*} date1 Première date à vérifier.
	 * @param {*} date2 Deuxième date à vérifier.
	 * @returns {boolean} True si la première est strictement inférieure à la deuxième.
	 */
	static isDayBefore(date1: number | string | Date, date2: number | string | Date): boolean {
		const d1 = this.toDateMidnight(date1);
		const d2 = this.toDateMidnight(date2);
		return d1.getTime() < d2.getTime();
	}

	/**
	 * Vérifie que deux dates sont sur la même journée.
	 * @param {*} date1 Première date.
	 * @param {*} date2 Deuxième date.
	 * @returns {boolean} True si les dates sont sur la même journée, false sinon.
	 */
	static isSameDay(date1: number | string | Date, date2: number | string | Date): boolean {
		const d1 = date1 instanceof Date ? date1 : new Date(date1);
		const d2 = date2 instanceof Date ? date2 : new Date(date2);
		const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
		const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());
		return utc1 === utc2;
	}

	/**
	 * Vérification qu'une date est inférieure ou égale à une autre (en
	 * tronquant à la journée).
	 * @param {*} date1 Première date à vérifier.
	 * @param {*} date2 Deuxième date à vérifier.
	 * @returns {boolean} True si la première est inférieure ou égale à la deuxième.
	 */
	static isDayBeforeOrSame(date1: number | string | Date, date2: number | string | Date): boolean {
		return this.isDayBefore(date1, date2) || this.isSameDay(date1, date2);
	}

	/**
	 * Ajoute un nombre de jours à une date donnée.
	 * @param date La date.
	 * @param inc le nombre de jours à incrémenter.
	 * @returns {Date} La nouvelle date incrémentée du nombre de jours.
	 */
	static dateAddDays(date, inc): Date {
		const d = new Date(date);
		d.setDate(d.getDate() + inc);
		return d;
	}

	/**
	 * Détermine le nombre de jours entre les deux dates passées en paramètre.
	 *
	 * @param date1 la première date.
	 * @param date2 la seconde date.
	 * @return le nombre de jours entre les deux dates.
	 */
	static nombreDeJoursEntreDates(date1: Date, date2: Date): number {
		const timestamp1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
		const timestamp2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
		return Math.abs(Math.floor((timestamp2 - timestamp1) / (1000 * 60 * 60 * 24)));
	}

	/**
	 * Retourne le nombre de semaines de l'année donnée.
	 * Règle issue de la norme ISO-8601, cf. https://www.calendrier.best/combien-de-semaine-dans-une-annee.html
	 *
	 * @param annee l'année.
	 * @return le nombre de semaines de l'année donnée.
	 */
	static nombreDeSemainesAnnee(annee: number): number {
		const premierJanvier = new Date(annee, 0, 1);

		// Une année contient 53 semaines si une des deux conditions suivantes est respectée :
		// - le 1er janvier est un jeudi ;
		// - le 1er janvier est un mercredi ET l'année est bisextile.
		const anneeA53Semaines = premierJanvier.getDay() === 4 || (premierJanvier.getDay() === 3 && DateUtils.estAnneeBissextile(annee));
		return anneeA53Semaines ? 53 : 52;
	}

	/**
	 * Détermine si l'année est bissextile ou non.
	 * Règle issue de l'ajustement du calendrier grégorien, cf. https://fr.wikipedia.org/wiki/Ann%C3%A9e_bissextile#R%C3%A8gle_actuelle
	 *
	 * @param annee l'année.
	 * @return true si l'année est bissextile, false sinon.
	 */
	static estAnneeBissextile(annee: number): boolean {
		return (annee % 4 === 0 && annee % 100 !== 0) || (annee % 400 === 0);
	}
}
